/**
 * checklists.js — the standing checklists' rules (Shopping · Backup · Cutting).
 * An item is one opsChecklistItems doc; `needed` = on the active list right now;
 * `hidden` = deleted from the checklist (✕) — kept so its name stays gone.
 */
import { normalizeName } from './names';

/**
 * Deleted from the checklist? An item someone put back on the active list counts as there
 * (a device still on the old app can't un-hide it, but can still add it).
 */
export const isHiddenItem = item => !!(item && item.hidden && !item.needed);

// name → item: the last one with that name, except that a visible item always wins
// over a deleted one.
function byName(items) {
  const m = new Map();
  (items || []).forEach(i => {
    const key = normalizeName(i.name);
    const prev = m.get(key);
    if (!prev || !isHiddenItem(i) || isHiddenItem(prev)) m.set(key, i);
  });
  return m;
}

/** Names (normalized) deleted from the checklist — no visible item carries them. */
export function hiddenNameKeys(items) {
  const out = new Set();
  byName(items).forEach((item, key) => { if (isHiddenItem(item)) out.add(key); });
  return out;
}

/** "Milk, Bread\nEggs" → ['Milk', 'Bread', 'Eggs'] — typing several at once. */
export function splitNames(text) {
  return String(text || '').split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
}

/**
 * Plan putting `names` on the active list — the one rule shared by the Shopping add box,
 * a store group's ＋, the Summary's "due to reorder" and Review's "Add to shopping".
 *
 * A name already in the catalog is flagged needed again (a new cycle starts `today`) — one
 * deleted from the checklist comes back; a new name becomes a new item. Repeats (any
 * spelling / case) count once. `storeFor(name)`
 * gives the shop to file it under ('' = none). An existing item's store is changed when
 * it differs — or, with `fillStoreOnly`, only set when it has none (Review never moves an
 * item to another shop).
 *
 * @returns {{ creates: [{ name, store }], updates: [{ id, patch, wasNeeded, wasHidden }] }}
 */
export function planChecklistAdds({ names, items, storeFor = () => '', fillStoreOnly = false, today }) {
  const byKey = byName(items);
  const seen = new Set();
  const creates = [];
  const updates = [];
  (names || []).forEach(name => {
    const key = normalizeName(name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const store = storeFor(name) || '';
    const existing = byKey.get(key);
    if (!existing) { creates.push({ name: String(name).trim(), store }); return; }
    const patch = { needed: true, neededAt: today };
    if (existing.hidden) patch.hidden = false;
    const setStore = fillStoreOnly ? !existing.store : existing.store !== store;
    if (store && setStore) patch.store = store;
    updates.push({ id: existing.id, patch, wasNeeded: !!existing.needed, wasHidden: !!existing.hidden });
  });
  return { creates, updates };
}

/**
 * The pick-from catalog: every saved item plus a "ghost" for each name only the history
 * still knows (so nothing you've ever bought goes missing), best first — frecency (how
 * often + how recently it was done), then most recently done, then A–Z. Items deleted
 * from the checklist are left out, and so are their names' ghosts.
 * `frecency` = Map(normName → score) from orderFrecency(log).
 */
export function checklistCatalog(items, log, frecency, query = '') {
  const byKey = byName(items);
  (log || []).forEach(l => {
    const key = normalizeName(l.name);
    if (key && !byKey.has(key)) byKey.set(key, { id: `hist:${key}`, name: l.name, ghost: true, lastDoneAt: l.date });
  });
  const q = normalizeName(query);
  const rows = [];
  byKey.forEach((item, key) => {
    if (isHiddenItem(item)) return;
    if (!q || key.includes(q)) rows.push({ item, key, score: frecency.get(key) || 0, last: item.lastDoneAt || '' });
  });
  rows.sort((a, b) => (b.score - a.score) || b.last.localeCompare(a.last) || a.item.name.localeCompare(b.item.name));
  return rows.map(r => r.item);
}
